import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';

import { useGetTunnelMessagesQuery } from 'api/main';
import { useSendKnowClientMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { ineligibleSet } from 'reducers/tunnel';
import { isStatusOk } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { EURO_SYMBOL, fundTypes } from 'utils/constants';
import { initKnowClientFPCI } from 'schemas/tunnel';

import { TunnelStep, TunnelBox } from '../../common';
import {
    ErrorMessage,
    CustomTextField,
    SubmitButton,
    RadioLabel,
    InfoButton,
    CheckboxLabel,
} from 'components';
import {
    FormControlLabel,
    RadioGroup,
    Typography,
    FormGroup,
    InputAdornment,
    Box,
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { useTunnelChangeHandlers } from 'utils/hooks';

const useStyles = makeStyles(theme => ({
    flexContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        columnGap: 16,
    },
    form: {
        maxWidth: 660,
        width: '100%',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    flexColumn: {
        flexDirection: 'column',
    },
    inputField: {
        marginRight: 10,
        marginBottom: 0,
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            width: 60,
        },
    },
    formControlLabel: {
        margin: '0 0 15px 0',
        color: theme.palette.grey[600],
    },
    heritageAmountLabel: {
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'baseline',
        },
    },
    heritageAmountField: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            maxWidth: 300,
        },
    },
    option: {
        fontSize: 16,
        alignItems: 'center',
        color: theme.palette.grey[600],
    },
}));

const labelSx = { display: 'block', flex: '1 0', mb: 2, color: 'common.black', fontSize: '16px' };

export const KYCSurveyPP = props => {
    const { handleNextStep, userStep } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const { data: messages } = useGetTunnelMessagesQuery();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const [sendKnowClient, { isSuccess: kycSuccess, isLoading: buttonLoader, reset: resetKYC }] =
        useSendKnowClientMutation();
    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;
    const isFCPR = tunnelSubscription?.investmentFund?.type === fundTypes.FCPR;

    const investmentSum = useMemo(
        () => tunnelSubscription?.investmentCharacteristics?.investingSum,
        [tunnelSubscription]
    );

    const kycSurvey = useMemo(() => tunnelSubscription?.kycSurvey, [tunnelSubscription]);
    const defaultForm = useMemo(
        () => initKnowClientFPCI(kycSurvey, isResubmit),
        [kycSurvey, isResubmit]
    );

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        annualNetIncome: '',
        incomeSource: { checkbox: '', other: '' },
        heritage: { amount: '', percentTotal: '' },
        debt: { amount: '', percentTotal: '' },
    });

    // effect to update default forms whenever the data on the server is updated
    useEffect(() => {
        setForm(defaultForm);
    }, [defaultForm]);

    const handleChange = useCallback(
        key => e => {
            if (key === 'annualNetIncome') {
                setForm(prevState => ({ ...prevState, [key]: e.target.value }));
            } else if (key === 'heritage' || key === 'debt') {
                const { value, name } = e.target;
                setForm(prevState => ({
                    ...prevState,
                    [key]: { ...prevState[key], [name]: value ? parseInt(value, 10) : '' },
                }));
            }
        },
        []
    );

    const handleIncomeSourceChange = useCallback(
        key =>
            ({ target: { name, checked, value } }) => {
                setForm(prevState => {
                    if (key === 'choice' && name === 'general_options.other' && !checked) {
                        return {
                            ...prevState,
                            incomeSource: {
                                choice: { ...prevState.incomeSource.choice, [name]: checked },
                                other: '',
                            },
                        };
                    }
                    return {
                        ...prevState,
                        incomeSource: {
                            ...prevState.incomeSource,
                            [key]:
                                key === 'choice'
                                    ? {
                                          ...prevState.incomeSource.choice,
                                          [name]: checked,
                                      }
                                    : value,
                        },
                    };
                });
            },
        []
    );

    const totalHeritagePercent = useMemo(
        () =>
            Object.keys(form.heritage)
                .slice(1)
                .reduce(
                    (accumulator, currentValue) => accumulator + (form.heritage[currentValue] || 0),
                    0
                ),
        [form.heritage]
    );

    const totalDebtPercent = useMemo(
        () =>
            Object.keys(form.debt)
                .slice(1)
                .reduce(
                    (accumulator, currentValue) => accumulator + (form.debt[currentValue] || 0),
                    0
                ),
        [form.debt]
    );

    const validateAnnualNetAmount = useCallback(() => {
        setErrors(prevState => ({
            ...prevState,
            annualNetIncome: form.annualNetIncome ? '' : texts.REMPLIER_CETT_ENTREE,
        }));
        return !!form.annualNetIncome;
    }, [form.annualNetIncome]);

    const validateIncomeSource = useCallback(() => {
        const isValid = Object.keys(form.incomeSource.choice)
            .slice(0, 6)
            .some(option => form.incomeSource.choice[option] === true);

        if (form.incomeSource.choice['general_options.other']) {
            setErrors(prevState => ({
                ...prevState,
                incomeSource: {
                    other: form.incomeSource.other ? '' : texts.REMPLIER_CETT_ENTREE,
                    checkbox: '',
                },
            }));
            return !!form.incomeSource.other;
        } else {
            setErrors(prevState => ({
                ...prevState,
                incomeSource: {
                    checkbox: isValid ? '' : texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                    other: '',
                },
            }));
            return isValid;
        }
    }, [form.incomeSource.choice, form.incomeSource.other]);

    const validateHeritage = useCallback(() => {
        const allInputsEmpty = Object.values(form.heritage)
            .slice(1)
            .every(formItem => !formItem);
        let amountError = '';

        if (!form.heritage.heritageAmount) {
            amountError = texts.SAISIR_LE_CHAMP;
        }
        if (allInputsEmpty) {
            setErrors(prevState => ({
                ...prevState,
                heritage: { amount: amountError, percentTotal: texts.SAISIR_LES_POURCENTAGES },
            }));
        } else if (totalHeritagePercent !== 100 && totalHeritagePercent > 0) {
            setErrors(prevState => ({
                ...prevState,
                heritage: { amount: amountError, percentTotal: texts.TOTAL_DES_POURCENTAGES },
            }));
        } else {
            setErrors(prevState => ({
                ...prevState,
                heritage: { amount: amountError, percentTotal: '' },
            }));
        }

        return totalHeritagePercent === 100 && form.heritage.heritageAmount;
    }, [totalHeritagePercent, form.heritage]);

    const validateDebt = useCallback(() => {
        const allInputsEmpty = Object.values(form.debt)
            .slice(1)
            .every(formItem => !formItem);

        let percentTotalError = '';

        if (allInputsEmpty || totalDebtPercent === 0) {
            percentTotalError = form.debt.indebtednessAmount ? texts.SAISIR_LES_POURCENTAGES : '';
        } else if (totalDebtPercent !== 100 && totalDebtPercent > 0) {
            percentTotalError = texts.TOTAL_DES_POURCENTAGES;
        } else if (form.debt.indebtednessAmount && totalDebtPercent !== 100) {
            percentTotalError = texts.TOTAL_DES_POURCENTAGES;
        }

        setErrors(prevState => ({
            ...prevState,
            debt: {
                amount:
                    form.debt.indebtednessAmount || form.debt.indebtednessAmount === 0
                        ? ''
                        : texts.SAISIR_LE_CHAMP,
                percentTotal: percentTotalError,
            },
        }));

        return (
            (totalDebtPercent === 100 && form.debt.indebtednessAmount > 0) ||
            (form.debt.indebtednessAmount === 0 && totalDebtPercent === 0)
        );
    }, [totalDebtPercent, form.debt]);

    const validateForm = useCallback(() => {
        if (tunnelSubscription.indirectUntransferred) {
            return (
                (!totalDebtPercent || totalDebtPercent === 100) &&
                (!totalHeritagePercent || totalHeritagePercent === 100)
            );
        } else {
            let netAmountValid = validateAnnualNetAmount();
            let incomeSourceValid = validateIncomeSource();
            let heritageValid = validateHeritage();
            let debtValid = validateDebt();
            return netAmountValid && incomeSourceValid && heritageValid && debtValid;
        }
    }, [
        tunnelSubscription.indirectUntransferred,
        totalDebtPercent,
        totalHeritagePercent,
        validateAnnualNetAmount,
        validateIncomeSource,
        validateHeritage,
        validateDebt,
    ]);

    const investmentSumWarning = useMemo(
        () => (isSLP || isFCPR ? false : investmentSum >= form.heritage.heritageAmount / 2),
        [investmentSum, form.heritage, isSLP, isFCPR]
    );

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            resetKYC();
            sendKnowClient({
                callback: afterSubmitCallback,
                ...form,
            });
        },
        [form, sendKnowClient, resetKYC]
    );

    const startSubmit = useCallback(
        afterSubmitCallback => () => {
            if (validateForm()) {
                if (investmentSumWarning && !tunnelSubscription?.indirectUntransferred) {
                    dispatch(
                        ineligibleSet({
                            open: true,
                            subMessage: texts.PROFIL_INVESTISSEUR_INCORRECT,
                            message: '',
                            canConfirm: true,
                            confirmMessage: texts.CONFIRM_INELLIGIBLE,
                            onConfirm: handleSubmit(),
                        })
                    );
                } else {
                    handleSubmit(afterSubmitCallback)();
                }
            }
        },
        [validateForm, investmentSumWarning, tunnelSubscription, dispatch, handleSubmit]
    );

    useEffect(() => {
        if (kycSuccess) {
            handleNextStep();
        }
    }, [kycSuccess, handleNextStep]);

    // this function returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => !isEqual(defaultForm, form), [form, defaultForm]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(startSubmit, checkChanges, isResubmit),
        [startSubmit, checkChanges, isResubmit, setTunnelChangesHandlers]
    );

    return (
        <TunnelStep
            title={texts.KYC_SURVEY}
            subtitle={isSLP ? texts.KYC_SURVEY_SLP_SUBTITLE : texts.KYC_SURVEY_FPCI_SUBTITLE}
        >
            <TunnelBox>
                <Box mb={3}>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography sx={labelSx}>{messages['step5.question_1.label']}</Typography>
                        <InfoButton
                            disableMarginBottom
                            title={texts.REVENUS}
                            content={
                                <React.Fragment>
                                    <Typography gutterBottom>{texts.REVENUS_P1}</Typography>
                                    <Typography gutterBottom>{texts.REVENUS_P2}</Typography>
                                    <Typography gutterBottom>{texts.REVENUS_P3}</Typography>
                                </React.Fragment>
                            }
                        />
                    </Box>
                    <RadioGroup
                        name="section_1"
                        className={classes.flexContainer}
                        onChange={handleChange('annualNetIncome')}
                        value={form.annualNetIncome}
                    >
                        <RadioLabel
                            label={messages['general_options.yes']}
                            value={messages['general_options.yes']}
                        />
                        <RadioLabel
                            label={messages['general_options.no']}
                            value={messages['general_options.no']}
                        />
                    </RadioGroup>
                    <ErrorMessage message={errors.annualNetIncome} />
                </Box>

                <Box mb={3}>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography sx={labelSx}>{messages['step5.question_2.label']}</Typography>
                        <InfoButton
                            disableMarginBottom
                            title={texts.SOURCE_PRINCIPALE}
                            content={texts.SOURCE_PRINCIPALE_INFO}
                        />
                    </Box>
                    <FormGroup className={classes.flexContainer}>
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <CheckboxLabel
                                className={classes.option}
                                label={messages[`step5.question_2.option_${i}`]}
                                checked={form.incomeSource.choice[`step5.question_2.option_${i}`]}
                                onChange={handleIncomeSourceChange('choice')}
                                key={`step5.question_2.option_${i}`}
                                name={`step5.question_2.option_${i}`}
                            />
                        ))}
                        <CheckboxLabel
                            className={classes.option}
                            label={messages['general_options.other']}
                            checked={form.incomeSource.choice['general_options.other']}
                            onChange={handleIncomeSourceChange('choice')}
                            name="general_options.other"
                        />
                        {form.incomeSource.choice['general_options.other'] && (
                            <CustomTextField
                                value={form.incomeSource.other}
                                onChange={handleIncomeSourceChange('other')}
                                error={errors.incomeSource.other}
                            />
                        )}
                    </FormGroup>
                    <ErrorMessage message={errors.incomeSource.checkbox} />
                </Box>

                <Box mb={3}>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                        <Typography sx={labelSx}>
                            {messages['step5.question_3.label_pp']}
                        </Typography>
                        <InfoButton
                            disableMarginBottom
                            title={texts.PATRIMOINE_GLOBAL}
                            content={
                                <React.Fragment>
                                    <Typography gutterBottom>{texts.PATRIMOINE_INFO_1}</Typography>
                                    <Typography gutterBottom>{texts.PATRIMOINE_INFO_2}</Typography>
                                </React.Fragment>
                            }
                        />
                    </Box>
                    <FormGroup className={clsx(classes.flexContainer, classes.flexColumn)}>
                        {Object.keys(form.heritage).map((heritageType, i) => (
                            <FormControlLabel
                                key={heritageType}
                                className={clsx(classes.formControlLabel, {
                                    [classes.heritageAmountLabel]: i === 0,
                                })}
                                control={
                                    <CustomTextField
                                        value={form.heritage[heritageType]}
                                        inputStyle={clsx(classes.inputField, {
                                            [classes.heritageAmountField]: i === 0,
                                        })}
                                        name={heritageType}
                                        onChange={handleChange('heritage')}
                                        error={i === 0 ? errors.heritage.amount : ''}
                                        showError={i === 0}
                                        type={i === 0 ? undefined : 'nubmer'}
                                        formatNumber={i === 0}
                                        endAdornment={
                                            <InputAdornment position="start">
                                                {i === 0 ? EURO_SYMBOL : '%'}
                                            </InputAdornment>
                                        }
                                    />
                                }
                                label={messages[`step5.question_3.option_${i + 1}`]}
                            />
                        ))}
                    </FormGroup>
                    <ErrorMessage message={errors.heritage.percentTotal} />
                </Box>

                <Typography sx={labelSx}>
                    {messages['step5.question_4.label_fcpi_pp_real']}
                </Typography>
                <FormGroup className={clsx(classes.flexContainer, classes.flexColumn)}>
                    {Object.keys(form.debt).map((debtType, i) => (
                        <FormControlLabel
                            key={debtType}
                            className={clsx(classes.formControlLabel, {
                                [classes.heritageAmountLabel]: i === 0,
                            })}
                            control={
                                <CustomTextField
                                    value={form.debt[debtType]}
                                    inputStyle={clsx(classes.inputField, {
                                        [classes.heritageAmountField]: i === 0,
                                    })}
                                    name={debtType}
                                    onChange={handleChange('debt')}
                                    error={i === 0 ? errors.debt.amount : ''}
                                    endAdornment={
                                        <InputAdornment position="start">
                                            {i === 0 ? EURO_SYMBOL : '%'}
                                        </InputAdornment>
                                    }
                                    type={i === 0 ? undefined : 'nubmer'}
                                    formatNumber={i === 0}
                                />
                            }
                            label={messages[`step5.question_4.option_${i + 1}_fcpi_pp_real`]}
                        />
                    ))}
                </FormGroup>
                <ErrorMessage message={errors.debt.percentTotal} />
            </TunnelBox>
            <SubmitButton
                text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                disabled={buttonLoader}
                onClick={changesMade || !hasStatusOk ? startSubmit() : () => handleNextStep(true)}
                loading={buttonLoader}
            />
        </TunnelStep>
    );
};

KYCSurveyPP.propTypes = {
    handleNextStep: PropTypes.func,
    userStep: PropTypes.object,
};

export default KYCSurveyPP;
